import {
  AvatarGroup,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardProps,
  Divider,
  Flex,
  Image,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { buildFlightPathString, copyText, Show } from '@pelicargo/ui';
import { getPrettyAircraftType } from '@pelicargo/utils';
import { useCallback } from 'react';
import { HiClipboardCopy } from 'react-icons/hi';
import { HiArrowPath } from 'react-icons/hi2';

import { TrpcOutputs } from '../config/trpc';
import { useRequestPickupOrigin } from '../hooks/request/useRequestPickupOrigin';
import { useCanChangeFlight } from '../hooks/useCanChangeFlight';
import { useFlightData } from '../hooks/useFlightData';
import { useFlightSegmentsHelper } from '../hooks/useFlightSegmentsHelper';
import { useSelectedQuoteVariant } from '../hooks/useSelectedQuoteVariant';
import { AirportStops } from './AirportStops';
import { QuoteScheduleModal } from './QuoteScheduleModal';
import { Timeline } from './Timeline';

type Props = CardProps & {
  quote: TrpcOutputs['getQuote'];
  quoteVariant?: TrpcOutputs['getQuote']['quote_variants'][0];
  showHeader?: boolean;
};

export const FlightJourneyCard = ({ quote, quoteVariant, showHeader, ...rest }: Props) => {
  const toast = useToast();

  const { modalDisclosure } = useSelectedQuoteVariant();
  const canChangeFlight = useCanChangeFlight(quote);

  const segments = quoteVariant?.flight_path?.length > 0 ? quoteVariant?.flight_path : quote?.flight_path;
  const pickupOrigin = useRequestPickupOrigin(quote.request);
  const { providers, mostConstrainedLeg } = useFlightSegmentsHelper(segments);
  const timelineData = useFlightData(segments, { pickupOrigin });

  const copySegments = useCallback(() => {
    const text = buildFlightPathString(segments ?? [], {
      pickupOrigin,
      originAirportId: quote?.origin_airport_id,
    });
    copyText(text);
    toast({ colorScheme: 'green', title: 'Copied!' });
  }, [segments, pickupOrigin, quote?.origin_airport_id, toast]);

  return (
    <>
      <Card h="auto" {...rest} px="4" pt="4" borderRadius="lg">
        <Stack divider={showHeader && <Divider />}>
          <Show if={showHeader}>
            <CardHeader w="full" p="0">
              <Stack spacing="2" pt="3">
                <Stack direction="row" align="center" spacing="6" justify="space-between">
                  <Stack direction="row" align="center" spacing="6" flex={1}>
                    <AvatarGroup size="sm" max={3}>
                      {providers.map(({ id, logo_uri }) => (
                        <Image key={id} src={logo_uri} alt="Airline logo" bg="bg.surface" boxSize="12" rounded="full" />
                      ))}
                    </AvatarGroup>
                    <Stack direction="row" maxW="300px" flex={1}>
                      <AirportStops flightSegments={segments} pickupOrigin={pickupOrigin} width="full" />
                    </Stack>
                    <Show if={!!timelineData?.timeInTransit || !!mostConstrainedLeg?.aircraft_type}>
                      <Text textStyle="captionRegular" align="left">
                        {timelineData?.timeInTransit}
                      </Text>
                      <Text textStyle="captionRegular" align="left">
                        {getPrettyAircraftType(mostConstrainedLeg?.aircraft_type)}
                      </Text>
                    </Show>
                  </Stack>
                </Stack>
                <Text textStyle="captionSmallRegular">{providers.map((provider) => provider?.name).join(', ')}</Text>
              </Stack>
            </CardHeader>
          </Show>
          <CardBody w="full" p="0" pb="4" role="group">
            <Stack h="full" justify="space-between">
              <Flex gap="2" justifyContent="flex-end">
                <Button
                  size="sm"
                  variant="ghost"
                  bg="bg.default"
                  leftIcon={<HiClipboardCopy />}
                  onClick={copySegments}
                  opacity="0"
                  _groupHover={{ opacity: 1 }}
                >
                  Copy
                </Button>
                <Show if={canChangeFlight}>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={<HiArrowPath />}
                    bg="bg.subtle"
                    onClick={modalDisclosure.onOpen}
                    _hover={{ bg: 'gray.200' }}
                  >
                    Change Flight
                  </Button>
                </Show>
              </Flex>
              <Stack direction="row" spacing="4" justify="space-between">
                <Timeline data={timelineData?.flightData} />
              </Stack>
              <Show if={!!quote?.estimated_transit_time}>
                <Stack justify="flex-end" align="flex-end" pb="4">
                  <Text textStyle="bodySmallRegular" align="left">
                    Estimated Transit: {quote?.estimated_transit_time}
                  </Text>
                </Stack>
              </Show>
            </Stack>
          </CardBody>
        </Stack>
      </Card>
      <QuoteScheduleModal quote={quote} {...modalDisclosure} />
    </>
  );
};
